import React__default, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { useLinkComponent } from '../../provider/Provider.js';
import { Avatar } from '../avatar/Avatar.js';
import { HappyWebSystemError, useQueryChildComponents } from '../base/SystemComponent.js';
import { Icon, __Icon } from '../icon/Icon.js';
import { LoadingSpinner } from '../loading/LoadingSpinner.js';
import { getThemeVariantBGColor, getThemeVariantContentColor, getThemeVariantBGHoverColor, isInverseVariant } from '../styles/stylePropTypes.js';

const StyledButton = styled.button`
  text-decoration: none;
  white-space: nowrap;
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  height: 3rem;
  padding: 0 1.25rem;
  min-width: 8.5rem;
  line-height: 3rem;
  border-radius: 2.5rem;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 600;
  background-color: ${getThemeVariantBGColor};
  color: ${getThemeVariantContentColor};
  ${(props)=>props.width === "fill" && "width: 100%;"}
  font-family: inherit;
  text-decoration: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  ${(props)=>props.$size === "small" && css`
      height: 2rem;
      border-radius: 0.25rem;
      padding: 0rem 1rem;
      min-width: 0.75rem;
    `}

  ${(props)=>props.$loading && css`
      color: transparent;

      & > [${Icon.dataId}] {
        opacity: 0;
      }
    `}

  &:hover,
  &:focus {
    background-color: ${getThemeVariantBGHoverColor};
    outline: none;
  }

  &:disabled,
  &[disabled] {
    cursor: not-allowed;
    pointer-events: none;
  }

  /* clearfix style hack to always force correct line height */
  &::after {
    content: '\\00a0 ';
    line-height: inherit;
    width: 0px;
  }

  & > [${Icon.dataId}] > * {
    margin-left: -0.5rem;
  }

  & [data-hpy-web-load-spinner] {
    position: absolute;
    top: calc(50% - 0.625rem);
    left: calc(50% - 0.625rem);
  }
  & [${LoadingSpinner.dataId}] circle {
    stroke: ${getThemeVariantContentColor};
  }

  & > [data-hpy-web-badge] {
    margin-right: 0.25rem;
  }
`;
const AvatarWrapper = styled(StyledButton)`
  border-radius: 50%;
  border: none;
  padding: 0;
  min-height: 0;
  min-width: 0;
  width: fit-content;
  height: fit-content;
  line-height: 0;
`;
const isAnchor = (externalLink, props)=>{
    return externalLink || typeof props?.to === "string" && props?.to.startsWith("http");
};
/**
 * Button
 *
 * Default action button used throughout app.
 *
 * TODO: remove font-size usage once no longer using font icon sheet
 *
 * @example
 * <Button>My Button</Button>
 * <Button>
 *  <Icon icon="icon-cancel" /> My Button
 * </Button>
 */ const Button = forwardRef(({ variant ="default" , size ="medium" , icon , testId ="X" , disabled , children , loading , externalLink =false , ...props }, ref)=>{
    if (!!disabled) {
        variant = isInverseVariant(variant) ? "disabled-inverse" : "disabled";
    }
    if (props.type === "submit" && props.form === undefined) {
        throw new HappyWebSystemError("Button", "When using a form submit button, it must be linked to a form id.");
    }
    if (props.to !== undefined && props?.onClick !== undefined) {
        throw new HappyWebSystemError("Button", "Only one of `to` or `onClick`is valid - this determines button semantic usage");
    }
    let element = "button";
    const linkComponent = useLinkComponent();
    if (props.to !== undefined) {
        element = linkComponent;
    }
    if (isAnchor(externalLink, props)) {
        props.href = props.to;
        props.target = "_blank";
        props.rel = "noreferrer";
        element = "a";
    }
    const [avatarChildren] = useQueryChildComponents(children, [
        Avatar
    ]);
    if (avatarChildren.length === 1) {
        return React__default.createElement(AvatarWrapper, {
            type: "button",
            ...props,
            ref: ref,
            "data-hpy-web-button": true,
            "data-test-id": "Button-" + testId,
            disabled: disabled || loading,
            variant: variant,
            "$size": size,
            "$loading": loading,
            as: element
        }, avatarChildren);
    }
    return React__default.createElement(StyledButton, {
        type: "button",
        ...props,
        ref: ref,
        "data-hpy-web-button": true,
        "data-test-id": "Button-" + testId,
        disabled: disabled || loading,
        variant: variant,
        "$size": size,
        "$loading": loading,
        as: element
    }, icon && React__default.createElement(__Icon, {
        icon: icon,
        size: size === "small" ? "xsmall" : size
    }), children, loading && React__default.createElement(LoadingSpinner, {
        size: "small"
    }));
});
Button.dataId = "data-hpy-web-button";
Button.displayName = "Button";

export { Button };
